import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBasesLocacionesStore } from "../basesStore";
import { apiErrorMessage } from "../services/basesApi";
import { notifyError } from "../../../components/ui";
import PublicPageToggle from "../components/PublicPageToggle";
import { buildThemeClasses } from "../../nuvex-access/theme";
import Modal from "../../../components/base/Modal";
import {
  useBasesStore,
  AMENITY_TYPES,
  type Amenity,
} from "../store";

// ---------------------------------------------------------------------------
// Amenidades — catalog + full profile editor (the "what": price, times, rules,
// photos, availability). Feeds Reservas and the public page through the store.
// ---------------------------------------------------------------------------

type Draft = Omit<Amenity, "id">;

const emptyDraft = (): Draft => ({
  name: "",
  type: "sala-de-juntas",
  description: "",
  location: "",
  price: 0,
  priceUnit: "por_reserva",
  currency: "MXN",
  capacity: 1,
  minMinutes: 60,
  maxMinutes: 120,
  openTime: "08:00",
  closeTime: "20:00",
  days: [],
  maxAdvanceDays: 30,
  bufferMinutes: 0,
  rules: "",
  cancellationPolicy: "",
  deposit: 0,
  requirements: "",
  photos: [],
  status: "Disponible",
  visiblePublic: true,
});

/** Editor state: the draft + photos pending upload (with local previews). */
interface EditingState {
  id?: string;
  draft: Draft;
  files: File[];
  previews: string[];
}

const PRICE_UNIT_LABELS: Record<string, string> = {
  por_reserva: "Por reserva",
  por_hora: "Por hora",
  por_bloque: "Por bloque",
};

const WEEKDAYS: { value: string; label: string }[] = [
  { value: "lun", label: "Lun" },
  { value: "mar", label: "Mar" },
  { value: "mie", label: "Mié" },
  { value: "jue", label: "Jue" },
  { value: "vie", label: "Vie" },
  { value: "sab", label: "Sáb" },
  { value: "dom", label: "Dom" },
];

export default function AmenidadesCatalog() {
  const { t } = useTranslation("common");
  const tc = buildThemeClasses();

  const amenities = useBasesStore((s) => s.amenities);
  const addAmenity = useBasesStore((s) => s.addAmenity);
  const updateAmenity = useBasesStore((s) => s.updateAmenity);
  const removeAmenity = useBasesStore((s) => s.removeAmenity);
  const bases = useBasesLocacionesStore((s) => s.bases);
  const { t: tb } = useTranslation("bases");

  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [saving, setSaving] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return amenities;
    return amenities.filter((a) => a.name.toLowerCase().includes(q));
  }, [amenities, query]);

  const openNew = () =>
    setEditing({ draft: emptyDraft(), files: [], previews: [] });
  const openEdit = (a: Amenity) =>
    setEditing({
      id: a.id,
      draft: { ...emptyDraft(), ...a, photos: [...a.photos] },
      files: [],
      previews: [],
    });
  const close = () => setEditing(null);

  const save = async () => {
    if (!editing || saving) return;
    const { id, draft, files } = editing;
    if (!draft.name.trim()) return;
    setSaving(true);
    try {
      if (id) await updateAmenity(id, draft, files);
      else await addAmenity(draft, files);
      close();
    } catch (err) {
      notifyError(apiErrorMessage(err, tb("common.saveError")));
    } finally {
      setSaving(false);
    }
  };

  const setField = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setEditing((prev) =>
      prev ? { ...prev, draft: { ...prev.draft, [key]: value } } : prev,
    );
  };

  const toggleDay = (day: string) =>
    setEditing((prev) => {
      if (!prev) return prev;
      const days = prev.draft.days ?? [];
      const next = days.includes(day)
        ? days.filter((d) => d !== day)
        : [...days, day];
      return { ...prev, draft: { ...prev.draft, days: next } };
    });

  // New photos are kept as File objects (uploaded to Storage on save) plus a
  // local preview; existing photos live in draft.photos.
  const onPhotos = (fileList: FileList | null) => {
    if (!fileList || !editing) return;
    Array.from(fileList)
      .slice(0, 6)
      .forEach((file) => {
        const reader = new FileReader();
        reader.onload = () => {
          const url = String(reader.result);
          setEditing((prev) =>
            prev
              ? {
                  ...prev,
                  files: [...prev.files, file],
                  previews: [...prev.previews, url],
                }
              : prev,
          );
        };
        reader.readAsDataURL(file);
      });
  };

  const removePhoto = (idx: number) =>
    setEditing((prev) =>
      prev
        ? {
            ...prev,
            draft: {
              ...prev.draft,
              photos: prev.draft.photos.filter((_, i) => i !== idx),
            },
          }
        : prev,
    );

  const removePending = (idx: number) =>
    setEditing((prev) =>
      prev
        ? {
            ...prev,
            files: prev.files.filter((_, i) => i !== idx),
            previews: prev.previews.filter((_, i) => i !== idx),
          }
        : prev,
    );

  const inputCls = `w-full rounded-lg border px-3 py-2 text-sm outline-none ${tc.border} ${tc.cardBg} ${tc.text}`;

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
              <i className="ri-community-line text-xl" />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>
                {t("apps.amenidades.name")}
              </h1>
              <p className={`text-sm ${tc.textSecondary}`}>
                {t("basesPropiedades.amenidades.subtitle")}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <PublicPageToggle />
            <div
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${tc.border} ${tc.cardBg}`}
            >
              <i className={`ri-search-line ${tc.textSecondary}`} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("basesPropiedades.search")}
                className={`bg-transparent text-sm outline-none ${tc.text}`}
              />
            </div>
            <button
              onClick={openNew}
              className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary transition-colors hover:bg-primary-400"
            >
              <i className="ri-add-line" />
              <span className="whitespace-nowrap">
                {t("basesPropiedades.amenidades.add")}
              </span>
            </button>
          </div>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((a) => (
            <div
              key={a.id}
              className={`overflow-hidden rounded-2xl border ${tc.cardBorder} ${tc.cardBg}`}
            >
              <div className="relative h-36 bg-gray-100 dark:bg-dark-card">
                {a.photos[0] ? (
                  <img
                    src={a.photos[0]}
                    alt={a.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-gray-400">
                    <i className="ri-image-line text-3xl" />
                  </div>
                )}
                <span className="absolute left-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-xs text-white">
                  {t(`basesPropiedades.amenityTypes.${a.type}`, a.type)}
                </span>
                <span
                  className={`absolute right-2 top-2 rounded-full px-2 py-0.5 text-xs text-white ${
                    a.status === "Disponible"
                      ? "bg-emerald-500"
                      : a.status === "Mantenimiento"
                        ? "bg-amber-500"
                        : "bg-gray-500"
                  }`}
                >
                  {t(`basesPropiedades.amenityStatus.${a.status}`, a.status)}
                </span>
              </div>
              <div className="p-4">
                <h3 className={`font-semibold ${tc.text}`}>{a.name}</h3>
                <p className={`mt-1 line-clamp-2 text-sm ${tc.textSecondary}`}>
                  {a.description || "—"}
                </p>
                <div className={`mt-3 flex items-center justify-between text-sm ${tc.textSecondary}`}>
                  <span>
                    <i className="ri-group-line mr-1" />
                    {a.capacity}
                  </span>
                  <span className="font-semibold text-primary-500">
                    {a.price > 0
                      ? `$${a.price} ${a.currency} · ${
                          PRICE_UNIT_LABELS[a.priceUnit ?? "por_reserva"]
                        }`
                      : t("basesPropiedades.free")}
                  </span>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <button
                    onClick={() => openEdit(a)}
                    className="flex-1 rounded-lg border border-primary-500 px-3 py-1.5 text-sm font-medium text-primary-500 hover:bg-primary-500/10"
                  >
                    <i className="ri-edit-line mr-1" />
                    {t("basesPropiedades.edit")}
                  </button>
                  <button
                    onClick={() => removeAmenity(a.id).catch((err) => notifyError(apiErrorMessage(err, tb("common.saveError"))))}
                    className="rounded-lg border border-red-500 px-3 py-1.5 text-sm text-red-500 hover:bg-red-500/10"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <p className={`col-span-full py-12 text-center text-sm ${tc.textSecondary}`}>
              {t("basesPropiedades.empty")}
            </p>
          )}
        </div>

        <p className={`mt-4 text-xs ${tc.textSecondary}`}>
          <i className="ri-information-line mr-1" />
          {t("basesPropiedades.amenidades.notice")}
        </p>
      </div>

      {/* Editor modal */}
      {editing && (
        <Modal
          title={
            editing.id
              ? `${t("basesPropiedades.edit")} — ${editing.draft.name || t("apps.amenidades.name")}`
              : t("basesPropiedades.amenidades.new")
          }
          onClose={close}
          themeClasses={tc}
          width="max-w-2xl w-full"
          content={
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className={`mb-1 block text-sm font-medium ${tc.text}`}>
                    {t("basesPropiedades.fields.name")}
                  </span>
                  <input
                    className={inputCls}
                    value={editing.draft.name}
                    onChange={(e) => setField("name", e.target.value)}
                  />
                </label>
                <label className="block">
                  <span className={`mb-1 block text-sm font-medium ${tc.text}`}>
                    {t("basesPropiedades.fields.type")}
                  </span>
                  <select
                    className={inputCls}
                    value={editing.draft.type}
                    onChange={(e) => setField("type", e.target.value)}
                  >
                    {AMENITY_TYPES.map((v) => (
                      <option key={v} value={v}>
                        {t(`basesPropiedades.amenityTypes.${v}`, v)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <label className="block">
                <span className={`mb-1 block text-sm font-medium ${tc.text}`}>
                  {t("basesPropiedades.fields.description")}
                </span>
                <textarea
                  className={inputCls}
                  rows={2}
                  value={editing.draft.description}
                  onChange={(e) => setField("description", e.target.value)}
                />
              </label>

              <label className="block">
                <span className={`mb-1 block text-sm font-medium ${tc.text}`}>
                  {t("basesPropiedades.fields.location")}
                </span>
                <input
                  className={inputCls}
                  placeholder={t("basesPropiedades.fields.locationPlaceholder")}
                  value={editing.draft.location ?? ""}
                  onChange={(e) => setField("location", e.target.value)}
                />
              </label>

              {/* Base (property_bases) donde vive la amenidad — CC-CONSOLE #1592 */}
              <label className="block">
                <span className={`mb-1 block text-sm font-medium ${tc.text}`}>
                  {tb("fields.base")}
                </span>
                <select
                  className={inputCls}
                  value={editing.draft.base_id ?? ""}
                  onChange={(e) => {
                    const base = bases.find((b) => b.id === e.target.value);
                    setEditing((cur) =>
                      cur
                        ? {
                            ...cur,
                            draft: {
                              ...cur.draft,
                              base_id: base?.id ?? null,
                              location: base ? base.name : cur.draft.location,
                            },
                          }
                        : cur,
                    );
                  }}
                >
                  <option value="">—</option>
                  {bases.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </label>

              {/* Pricing — precio opcional (0 = sin costo) */}
              <fieldset className={`rounded-lg border p-3 ${tc.border}`}>
                <legend className={`px-1 text-sm font-semibold ${tc.text}`}>
                  {t("basesPropiedades.sections.pricing")}
                </legend>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <label className="block">
                    <span className={`mb-1 block text-sm font-medium ${tc.text}`}>
                      {t("basesPropiedades.fields.price")}
                    </span>
                    <input
                      type="number"
                      min={0}
                      className={inputCls}
                      value={editing.draft.price}
                      onChange={(e) => setField("price", Number(e.target.value))}
                    />
                  </label>
                  <label className="block">
                    <span className={`mb-1 block text-sm font-medium ${tc.text}`}>
                      {t("basesPropiedades.fields.priceUnit")}
                    </span>
                    <select
                      className={inputCls}
                      value={editing.draft.priceUnit ?? "por_reserva"}
                      onChange={(e) =>
                        setField("priceUnit", e.target.value as Amenity["priceUnit"])
                      }
                    >
                      {Object.entries(PRICE_UNIT_LABELS).map(([v, l]) => (
                        <option key={v} value={v}>
                          {l}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className={`mb-1 block text-sm font-medium ${tc.text}`}>
                      {t("basesPropiedades.fields.currency")}
                    </span>
                    <input
                      className={inputCls}
                      value={editing.draft.currency}
                      onChange={(e) => setField("currency", e.target.value)}
                    />
                  </label>
                  <label className="block">
                    <span className={`mb-1 block text-sm font-medium ${tc.text}`}>
                      {t("basesPropiedades.fields.deposit")}
                    </span>
                    <input
                      type="number"
                      min={0}
                      className={inputCls}
                      value={editing.draft.deposit ?? 0}
                      onChange={(e) => setField("deposit", Number(e.target.value))}
                    />
                  </label>
                </div>
                <p className={`mt-2 text-xs ${tc.textSecondary}`}>
                  <i className="ri-information-line mr-1" />
                  {t("basesPropiedades.fields.priceHint")}
                </p>
              </fieldset>

              {/* Reservation times */}
              <fieldset className={`rounded-lg border p-3 ${tc.border}`}>
                <legend className={`px-1 text-sm font-semibold ${tc.text}`}>
                  {t("basesPropiedades.sections.times")}
                </legend>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <label className="block">
                    <span className={`mb-1 block text-sm font-medium ${tc.text}`}>
                      {t("basesPropiedades.fields.minMinutes")}
                    </span>
                    <input
                      type="number"
                      min={0}
                      step={15}
                      className={inputCls}
                      value={editing.draft.minMinutes}
                      onChange={(e) => setField("minMinutes", Number(e.target.value))}
                    />
                  </label>
                  <label className="block">
                    <span className={`mb-1 block text-sm font-medium ${tc.text}`}>
                      {t("basesPropiedades.fields.maxMinutes")}
                    </span>
                    <input
                      type="number"
                      min={0}
                      step={15}
                      className={inputCls}
                      value={editing.draft.maxMinutes}
                      onChange={(e) => setField("maxMinutes", Number(e.target.value))}
                    />
                  </label>
                  <label className="block">
                    <span className={`mb-1 block text-sm font-medium ${tc.text}`}>
                      {t("basesPropiedades.fields.openTime")}
                    </span>
                    <input
                      type="time"
                      className={inputCls}
                      value={editing.draft.openTime}
                      onChange={(e) => setField("openTime", e.target.value)}
                    />
                  </label>
                  <label className="block">
                    <span className={`mb-1 block text-sm font-medium ${tc.text}`}>
                      {t("basesPropiedades.fields.closeTime")}
                    </span>
                    <input
                      type="time"
                      className={inputCls}
                      value={editing.draft.closeTime}
                      onChange={(e) => setField("closeTime", e.target.value)}
                    />
                  </label>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <label className="block">
                    <span className={`mb-1 block text-sm font-medium ${tc.text}`}>
                      {t("basesPropiedades.fields.maxAdvanceDays")}
                    </span>
                    <input
                      type="number"
                      min={0}
                      className={inputCls}
                      value={editing.draft.maxAdvanceDays ?? 30}
                      onChange={(e) =>
                        setField("maxAdvanceDays", Number(e.target.value))
                      }
                    />
                  </label>
                  <label className="block">
                    <span className={`mb-1 block text-sm font-medium ${tc.text}`}>
                      {t("basesPropiedades.fields.bufferMinutes")}
                    </span>
                    <input
                      type="number"
                      min={0}
                      step={15}
                      className={inputCls}
                      value={editing.draft.bufferMinutes ?? 0}
                      onChange={(e) =>
                        setField("bufferMinutes", Number(e.target.value))
                      }
                    />
                  </label>
                </div>
                <div className="mt-3">
                  <span className={`mb-1 block text-sm font-medium ${tc.text}`}>
                    {t("basesPropiedades.fields.days")}
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {WEEKDAYS.map((d) => {
                      const active = (editing.draft.days ?? []).includes(d.value);
                      return (
                        <button
                          key={d.value}
                          type="button"
                          onClick={() => toggleDay(d.value)}
                          className={`rounded-lg border px-3 py-1.5 text-sm ${
                            active
                              ? "border-primary-500 bg-primary-500/10 text-primary-500"
                              : `${tc.border} ${tc.textSecondary}`
                          }`}
                        >
                          {d.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </fieldset>

              {/* Rules / policies */}
              <fieldset className={`rounded-lg border p-3 ${tc.border}`}>
                <legend className={`px-1 text-sm font-semibold ${tc.text}`}>
                  {t("basesPropiedades.sections.rules")}
                </legend>
                <label className="block">
                  <span className={`mb-1 block text-sm font-medium ${tc.text}`}>
                    {t("basesPropiedades.fields.rules")}
                  </span>
                  <textarea
                    className={inputCls}
                    rows={2}
                    value={editing.draft.rules}
                    onChange={(e) => setField("rules", e.target.value)}
                  />
                </label>
                <label className="mt-3 block">
                  <span className={`mb-1 block text-sm font-medium ${tc.text}`}>
                    {t("basesPropiedades.fields.cancellationPolicy")}
                  </span>
                  <textarea
                    className={inputCls}
                    rows={2}
                    value={editing.draft.cancellationPolicy ?? ""}
                    onChange={(e) =>
                      setField("cancellationPolicy", e.target.value)
                    }
                  />
                </label>
                <label className="mt-3 block">
                  <span className={`mb-1 block text-sm font-medium ${tc.text}`}>
                    {t("basesPropiedades.fields.requirements")}
                  </span>
                  <textarea
                    className={inputCls}
                    rows={2}
                    value={editing.draft.requirements ?? ""}
                    onChange={(e) => setField("requirements", e.target.value)}
                  />
                </label>
              </fieldset>

              {/* State + public visibility */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className={`mb-1 block text-sm font-medium ${tc.text}`}>
                    {t("basesPropiedades.fields.status")}
                  </span>
                  <select
                    className={inputCls}
                    value={editing.draft.status}
                    onChange={(e) =>
                      setField("status", e.target.value as Amenity["status"])
                    }
                  >
                    <option value="Disponible">
                      {t("basesPropiedades.amenityStatus.Disponible")}
                    </option>
                    <option value="Mantenimiento">
                      {t("basesPropiedades.amenityStatus.Mantenimiento")}
                    </option>
                    <option value="Oculta">
                      {t("basesPropiedades.amenityStatus.Oculta")}
                    </option>
                  </select>
                </label>
                <label className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={editing.draft.visiblePublic !== false}
                    onChange={(e) =>
                      setField("visiblePublic", e.target.checked)
                    }
                  />
                  <span className={`text-sm font-medium ${tc.text}`}>
                    {t("basesPropiedades.fields.visiblePublic")}
                  </span>
                </label>
              </div>

              {/* Photos */}
              <div>
                <span className={`mb-1 block text-sm font-medium ${tc.text}`}>
                  {t("basesPropiedades.fields.photos")}
                </span>
                <div className="flex flex-wrap gap-2">
                  {editing.draft.photos.map((p, i) => (
                    <div key={`ex-${i}`} className="relative h-20 w-20 overflow-hidden rounded-lg">
                      <img src={p} alt="" className="h-full w-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removePhoto(i)}
                        className="absolute right-0 top-0 bg-black/60 px-1 text-white"
                      >
                        <i className="ri-close-line text-xs" />
                      </button>
                    </div>
                  ))}
                  {editing.previews.map((p, i) => (
                    <div key={`new-${i}`} className="relative h-20 w-20 overflow-hidden rounded-lg ring-2 ring-primary-500">
                      <img src={p} alt="" className="h-full w-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removePending(i)}
                        className="absolute right-0 top-0 bg-black/60 px-1 text-white"
                      >
                        <i className="ri-close-line text-xs" />
                      </button>
                    </div>
                  ))}
                  <label
                    className={`flex h-20 w-20 cursor-pointer items-center justify-center rounded-lg border border-dashed ${tc.border} ${tc.textSecondary}`}
                  >
                    <i className="ri-upload-2-line text-xl" />
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => onPhotos(e.target.files)}
                    />
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={close}
                  disabled={saving}
                  className={`rounded-lg border px-4 py-2 text-sm ${tc.border} ${tc.text} disabled:opacity-50`}
                >
                  {t("basesPropiedades.cancel")}
                </button>
                <button
                  type="button"
                  onClick={save}
                  disabled={saving || !editing.draft.name.trim()}
                  className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary hover:bg-primary-400 disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <i className="ri-loader-4-line mr-1 animate-spin" />
                      {t("basesPropiedades.saving")}
                    </>
                  ) : (
                    t("basesPropiedades.save")
                  )}
                </button>
              </div>
            </div>
          }
        />
      )}
    </div>
  );
}
